import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Clock3, Copy, ExternalLink, Key, LockKeyhole, RefreshCw, ShieldAlert, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container, meta } from "@/components/store/page-parts";
import { toast } from "sonner";

export const Route = createFileRoute("/account/orders/$orderId")({
  head: () => meta("Order Details — betteraccess.store", "View digital delivery status and setup instructions."),
  component: Order,
});

function Order() {
  const { orderId } = Route.useParams();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`/api/orders/status/${encodeURIComponent(orderId)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.order) setOrder(data.order);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void fetchStatus();
    const timer = setInterval(() => {
      if (order?.payment_status === "pending" || order?.fulfillment_status === "pending") {
        void fetchStatus();
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [orderId]);

  let deliveredItems: string[] = [];
  if (order?.delivered_data) {
    try {
      const parsed = typeof order.delivered_data === "string" ? JSON.parse(order.delivered_data) : order.delivered_data;
      if (Array.isArray(parsed)) {
        deliveredItems = parsed;
      } else if (typeof parsed === "string") {
        deliveredItems = [parsed];
      }
    } catch {
      deliveredItems = [String(order.delivered_data)];
    }
  }

  const isPaid = order?.payment_status === "paid";
  const isDelivered = order?.fulfillment_status === "delivered";

  return (
    <Container className="py-10">
      <div className="flex items-center justify-between">
        <Link to="/account/orders" className="text-sm font-bold text-primary hover:underline">
          ← Back to orders
        </Link>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setRefreshing(true);
            void fetchStatus();
          }}
          disabled={refreshing}
        >
          <RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh status
        </Button>
      </div>

      <div className="mt-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-mono uppercase text-muted-foreground">ORDER ID: {orderId}</p>
          <h1 className="mt-2 text-3xl font-black">{order?.product_name || "Digital Access Order"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Customer: {order?.customer_email || "Registered customer"} · Quantity: {order?.quantity || 1} · Total:{" "}
            {order?.currency === "USD" ? `$${order?.total_amount?.toFixed(2)}` : `৳${order?.total_amount?.toFixed(0)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <span
            className={`rounded px-3 py-1.5 text-xs font-extrabold uppercase ${
              isPaid ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"
            }`}
          >
            Payment: {order?.payment_status || "Pending"}
          </span>
          <span
            className={`rounded px-3 py-1.5 text-xs font-extrabold uppercase ${
              isDelivered ? "bg-blue-500/10 text-blue-500" : "bg-slate-500/10 text-slate-400"
            }`}
          >
            Delivery: {order?.fulfillment_status || "Processing"}
          </span>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_340px]">
        <section className="rounded-lg border bg-card p-5 sm:p-7">
          <div className="flex items-center gap-3">
            <LockKeyhole className="size-6 text-primary" />
            <div>
              <h2 className="text-lg font-black">Delivered Digital Credentials & Keys</h2>
              <p className="text-xs text-muted-foreground">Automated instant allocation from verified stock</p>
            </div>
          </div>

          {isDelivered && deliveredItems.length > 0 ? (
            <div className="mt-6 space-y-3">
              {deliveredItems.map((item, index) => (
                <div key={index} className="rounded-lg bg-ink p-4 text-ink-foreground">
                  <div className="flex items-center justify-between text-[11px] font-bold text-ink-muted">
                    <span>ITEM #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(item);
                        toast.success("Copied to clipboard!");
                      }}
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      <Copy className="size-3" /> Copy
                    </button>
                  </div>
                  <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-sm font-semibold">{item}</pre>
                </div>
              ))}
            </div>
          ) : !isPaid ? (
            <div className="mt-6 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-6 text-center">
              <Clock3 className="mx-auto size-8 text-amber-500" />
              <p className="mt-3 font-bold text-amber-500">Awaiting Payment Confirmation</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Once payment is confirmed, your credentials and instructions will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed p-6 text-center">
              <RefreshCw className="mx-auto size-8 animate-spin text-primary" />
              <p className="mt-3 font-bold">Fulfilling your digital order...</p>
              <p className="mt-1 text-xs text-muted-foreground">This normally takes less than 30 seconds. Please refresh shortly.</p>
            </div>
          )}

          <h2 className="mt-8 font-black">Activation & Setup Guide</h2>
          <ol className="mt-4 space-y-3 text-sm text-muted-foreground">
            {[
              "Copy the provided credentials or activation link from above.",
              "Open in a clean browser window or in the corresponding official app.",
              "If you need any guidance or have any questions, our team is available 24/7 on WhatsApp.",
            ].map((x, i) => (
              <li key={x} className="flex gap-3">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {i + 1}
                </span>
                <span>{x}</span>
              </li>
            ))}
          </ol>
        </section>

        <aside className="rounded-lg border bg-card p-5">
          <h2 className="font-black">Order Timeline</h2>
          <div className="mt-5 space-y-5">
            {[
              { title: "Order received", done: true },
              { title: "Payment confirmed", done: isPaid },
              { title: "Credentials allocated & delivered", done: isDelivered },
            ].map(step => (
              <div key={step.title} className="flex items-center gap-3 text-sm font-semibold">
                <span
                  className={`grid size-6 place-items-center rounded-full ${
                    step.done ? "bg-success text-primary-foreground" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  <Check className="size-3" />
                </span>
                <span className={step.done ? "text-foreground" : "text-muted-foreground"}>{step.title}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 border-t pt-5">
            <p className="text-xs font-bold text-muted-foreground">Need help with this order?</p>
            <Button asChild variant="outline" className="mt-3 w-full">
              <Link to="/support">
                Contact WhatsApp Support <ExternalLink className="size-3.5" />
              </Link>
            </Button>
          </div>
        </aside>
      </div>
    </Container>
  );
}
